import React, { useState } from "react";
import { addVariantToCart } from "@/lib/shopifyCart";

/**
 * GoldSingleRow — compact catalog list row for the $10–$19 and $9 & Under
 * buckets. Slimmed row height + smaller Add to Cart button per the
 * shop UX pass (buttons must not dominate).
 *
 * Desktop grid columns:
 *   col 1 · code      (58px)
 *   col 2 · name      (flex, min-w-0 → truncates when long, clickable)
 *   col 3 · country   (52px)
 *   col 4 · type      (78px)
 *   col 5 · price     (68px, right-aligned)
 *   col 6 · button    (98px, right-aligned, slim)
 *
 * Mobile grid (3-col):
 *   row 1 → [ code ] [ name/country/type stack (clickable) ] [ price ]
 *   row 2 → [ ADD TO CART spanning 3, slim ]
 *
 * All product-detail links open in the SAME tab (no target="_blank").
 */
export default function GoldSingleRow({ product }) {
  const {
    sticker_code, subject_name, player_name, country_code, sticker_type,
    product_title, price, availability,
    shopify_variant_id, product_url,
  } = product || {};

  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");

  const soldOut = availability === "SOLD_OUT";
  const canAdd  = Boolean(shopify_variant_id) && !soldOut;
  const displayName = player_name || subject_name || product_title || "";
  const typeChip =
    sticker_type === "fwc"        ? "FWC" :
    sticker_type === "team-badge" ? "TEAM BADGE" :
                                    "REGULAR";

  const onAdd = async () => {
    if (!canAdd || busy) return;
    setBusy(true); setErr("");
    const res = await addVariantToCart(shopify_variant_id, 1);
    setBusy(false);
    if (!res.ok) setErr("Cart error — refresh.");
  };

  const ctaLabel = !shopify_variant_id
    ? "UNAVAILABLE"
    : soldOut ? "SOLD OUT"
    : busy    ? "ADDING…"
    :           "ADD TO CART";

  const linkProps = product_url ? { href: product_url } : {};

  const NameBlock = (
    <>
      <div className="truncate text-[13px] md:text-[13px] font-medium text-[var(--ink)] leading-tight">
        {displayName}
      </div>
      <div className="text-[10px] uppercase font-mono tracking-wide text-[var(--smoke)] md:hidden">
        {(country_code || "GOLD")} · {typeChip}
      </div>
    </>
  );

  return (
    <div
      data-testid={`gold-single-row-${sticker_code || "x"}`}
      className="border-b border-neutral-200 py-1.5 md:py-1.5 grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[58px_minmax(0,1fr)_52px_78px_68px_98px] items-center gap-x-3 gap-y-0.5"
    >
      {/* col 1 · sticker code */}
      <div className="md:col-start-1 font-mono text-[11px] md:text-xs font-semibold uppercase tracking-wide text-[var(--ink)]"
           data-testid={`gold-single-row-code-${sticker_code || "x"}`}>
        {sticker_code || "—"}
      </div>

      {/* col 2 · name (clickable, opens product in same tab) */}
      <div className="md:col-start-2 min-w-0">
        {product_url ? (
          <a {...linkProps}
             className="block hover:underline"
             data-testid={`gold-single-row-title-${sticker_code || "x"}`}>
            {NameBlock}
          </a>
        ) : NameBlock}
      </div>

      {/* col 3 · country (desktop only) */}
      <div className="hidden md:block md:col-start-3 text-[11px] font-mono uppercase text-[var(--smoke)] truncate">
        {country_code || "—"}
      </div>

      {/* col 4 · type (desktop only) */}
      <div className="hidden md:block md:col-start-4 text-[10px] font-mono uppercase tracking-wide text-[var(--smoke)] truncate">
        {typeChip}
      </div>

      {/* col 5 · price */}
      <div className="col-start-3 md:col-start-5 md:text-right text-sm font-display font-semibold text-[var(--ink)] whitespace-nowrap"
           data-testid={`gold-single-row-price-${sticker_code || "x"}`}>
        {price || "—"}
      </div>

      {/* col 6 · ADD TO CART (slim) */}
      <div className="col-span-3 md:col-span-1 md:col-start-6 md:justify-self-end mt-0.5 md:mt-0 w-full md:w-auto">
        <button
          type="button"
          onClick={onAdd}
          disabled={!canAdd || busy}
          aria-disabled={!canAdd || busy}
          className={`btn-yellow text-[10px] py-0.5 px-2 leading-none whitespace-nowrap w-full md:w-[98px] h-7 justify-center ${(!canAdd || busy) ? "opacity-60 cursor-not-allowed" : ""}`}
          data-variant-id={shopify_variant_id || ""}
          data-testid={`gold-single-row-cta-${sticker_code || "x"}`}
        >
          {ctaLabel}
        </button>
      </div>

      {err ? (
        <div role="alert" className="col-span-3 md:col-span-6 text-[10px] text-red-700"
             data-testid={`gold-single-row-error-${sticker_code || "x"}`}>
          {err}
        </div>
      ) : null}
    </div>
  );
}
